import { useMemo, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Modal } from '../../../../shared/components/Modal'
import { useToast } from '../../../../shared/components/toast'
import { handleApiError } from '../../../../shared/utils/handleApiError'
import { useAuthStore } from '../../../auth/store'
import { useMerchant } from '../../../merchants/hooks/useMerchants'
import { IntakeFieldRenderer, validateIntake } from '../../../../shared/components/IntakeFieldRenderer'
import {
  intakeApi,
  buildPurchasePackagePayload,
  schemaFields,
  unwrapList,
  type BookingServiceSummary,
  type CheckoutResult,
  type IntakeDomain,
  type PackageListingSummary,
} from '../../api/intakeApi'

interface Props {
  isOpen: boolean
  onClose: () => void
  /**
   * Pin the modal to a single intake domain and hide the type selector. Used by the
   * "Bespoke Order" entry point to jump straight into a schema-driven CUSTOM_ORDER
   * draft (the legacy NewBespokeOrderModal → POST /orders/bespoke is retired, PE-1974).
   */
  lockDomain?: IntakeDomain
}

const DOMAINS: { value: IntakeDomain; label: string }[] = [
  { value: 'CUSTOM_ORDER', label: 'Custom order' },
  { value: 'BOOKING', label: 'Booking' },
  { value: 'PACKAGE', label: 'Package' },
]

const field = 'w-full rounded-md border border-border px-3 py-2 text-sm focus:border-gray-500 focus:outline-none'

/** Admin "create an order on behalf of a customer" — schema-driven (PE-1889). */
export function NewIntakeOrderModal({ isOpen, onClose, lockDomain }: Props) {
  const { toast } = useToast()
  const merchantId = useAuthStore((s) => s.selectedTenantId)
  const { data: merchant } = useMerchant(merchantId ?? '')
  const solutionId = merchant?.solutionId ?? ''

  const [domain, setDomain] = useState<IntakeDomain>(lockDomain ?? 'CUSTOM_ORDER')
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [serviceId, setServiceId] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [medium, setMedium] = useState('VIDEO_CALL')
  const [packageListingId, setPackageListingId] = useState('')
  const [formData, setFormData] = useState<Record<string, unknown>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null)
  const [customOrderCreated, setCustomOrderCreated] = useState(false)

  const ready = !!merchantId && !!solutionId
  const schemaQ = useQuery({
    queryKey: ['field-config', merchantId, solutionId, domain],
    queryFn: () => intakeApi.getFieldSchema(merchantId!, solutionId, domain),
    enabled: isOpen && ready,
  })
  const fields = useMemo(() => schemaFields(schemaQ.data), [schemaQ.data])

  const servicesQ = useQuery({
    queryKey: ['booking', 'services', merchantId],
    queryFn: () => intakeApi.listBookingServices(),
    enabled: isOpen && ready && domain === 'BOOKING',
  })
  const listingsQ = useQuery({
    queryKey: ['packages', 'listings', merchantId],
    queryFn: () => intakeApi.listPackageListings(),
    enabled: isOpen && ready && domain === 'PACKAGE',
  })

  const reset = () => {
    setCustomerEmail(''); setCustomerName(''); setServiceId(''); setScheduledAt('')
    setMedium('VIDEO_CALL'); setPackageListingId(''); setFormData({}); setErrors({}); setCheckoutUrl(null)
    setCustomOrderCreated(false)
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (domain === 'BOOKING') {
        return intakeApi.createBooking({ serviceId, scheduledAt: new Date(scheduledAt).toISOString(), medium, customerName, customerEmail, solutionId, formData })
      }
      if (domain === 'PACKAGE') {
        return intakeApi.purchasePackage(buildPurchasePackagePayload({ packageListingId, customerEmail, formData }))
      }
      return intakeApi.createCustomOrder({ customerEmail, intake: formData, solutionId })
    },
    onSuccess: (res) => {
      const url = (res as CheckoutResult | undefined)?.checkoutUrl
      if (url) {
        setCheckoutUrl(url)
        toast.success('Order created', 'A payment link is ready to share.')
        return
      }
      // A bespoke order starts unpriced, so no payment link exists yet — it is
      // minted from the custom order's detail view once priced + marked ready.
      if (domain === 'CUSTOM_ORDER') {
        setCustomOrderCreated(true)
        toast.success('Bespoke order created', 'Price it to issue the customer a payment link.')
        return
      }
      toast.success('Created', 'The order was created on the customer’s behalf.')
      onClose(); reset()
    },
    onError: (e) => toast.error('Could not create the order', handleApiError(e)),
  })

  const submit = () => {
    const e: Record<string, string> = {}
    if (!customerEmail.trim()) e.customerEmail = 'Customer email is required'
    if (domain === 'BOOKING') {
      if (!customerName.trim()) e.customerName = 'Customer name is required'
      if (!serviceId) e.serviceId = 'Choose a service'
      if (!scheduledAt) e.scheduledAt = 'Choose a date & time'
    }
    if (domain === 'PACKAGE' && !packageListingId) e.packageListingId = 'Choose a package'
    Object.assign(e, validateIntake(fields, formData))
    setErrors(e)
    if (Object.keys(e).length === 0) mutation.mutate()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <h2 className="mb-1 text-lg font-semibold text-foreground">
          {lockDomain === 'CUSTOM_ORDER' ? 'New bespoke order' : 'New order on behalf of a customer'}
        </h2>
        <p className="mb-5 text-sm text-muted-foreground">
          {merchant ? `Merchant: ${merchant.name ?? merchantId}` : 'Select a merchant to begin.'}
        </p>

        {!merchantId && <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">Select a merchant first.</p>}
        {merchantId && merchant && !solutionId && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
            This merchant’s profile is missing its solution assignment. Assign a solution to the merchant before creating orders on their behalf.
          </p>
        )}

        {customOrderCreated ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-foreground">
              The bespoke order was created for {customerEmail}. It has no price yet, so there is no
              payment link to share.
            </p>
            <ol className="ml-4 list-decimal text-sm text-muted-foreground">
              <li>Open it under Custom Orders and set a price.</li>
              <li>Mark it as ready — this issues the invoice and notifies the customer.</li>
              <li>Copy the payment link from the order to share it directly.</li>
            </ol>
            <div className="flex gap-2">
              <button onClick={() => { onClose(); reset() }} className="rounded-md bg-gray-900 px-4 py-2 text-sm text-white">Done</button>
              <button onClick={reset} className="rounded-md border border-border px-4 py-2 text-sm">Create another</button>
            </div>
          </div>
        ) : checkoutUrl ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-foreground">Share this payment link with the customer:</p>
            <input readOnly value={checkoutUrl} className={field} onFocus={(ev) => ev.currentTarget.select()} />
            <div className="flex gap-2">
              <a href={checkoutUrl} target="_blank" rel="noreferrer" className="rounded-md bg-gray-900 px-4 py-2 text-sm text-white">Open</a>
              <button onClick={() => { onClose(); reset() }} className="rounded-md border border-border px-4 py-2 text-sm">Done</button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {!lockDomain && (
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Type</label>
                <select className={field} value={domain} onChange={(e) => { setDomain(e.target.value as IntakeDomain); setErrors({}); setFormData({}) }}>
                  {DOMAINS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">Customer email <span className="text-red-500">*</span></label>
              <input type="email" className={field} value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
              {errors.customerEmail && <p className="mt-1 text-xs text-red-600">{errors.customerEmail}</p>}
            </div>

            {domain === 'BOOKING' && (
              <>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Customer name <span className="text-red-500">*</span></label>
                  <input className={field} value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                  {errors.customerName && <p className="mt-1 text-xs text-red-600">{errors.customerName}</p>}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Service <span className="text-red-500">*</span></label>
                  <select className={field} value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
                    <option value="" disabled>Select…</option>
                    {unwrapList<BookingServiceSummary>(servicesQ.data).map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                  </select>
                  {errors.serviceId && <p className="mt-1 text-xs text-red-600">{errors.serviceId}</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">Date &amp; time <span className="text-red-500">*</span></label>
                    <input type="datetime-local" className={field} value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                    {errors.scheduledAt && <p className="mt-1 text-xs text-red-600">{errors.scheduledAt}</p>}
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">Format</label>
                    <select className={field} value={medium} onChange={(e) => setMedium(e.target.value)}>
                      <option value="VIDEO_CALL">Video call</option>
                      <option value="IN_PERSON">In person</option>
                      <option value="PHONE_CALL">Phone call</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {domain === 'PACKAGE' && (
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Package <span className="text-red-500">*</span></label>
                <select className={field} value={packageListingId} onChange={(e) => setPackageListingId(e.target.value)}>
                  <option value="" disabled>Select…</option>
                  {unwrapList<PackageListingSummary>(listingsQ.data).map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
                {errors.packageListingId && <p className="mt-1 text-xs text-red-600">{errors.packageListingId}</p>}
              </div>
            )}

            {fields.length > 0 && (
              <div className="border-t border-border pt-4">
                <IntakeFieldRenderer fields={fields} values={formData} errors={errors} onChange={(k, v) => setFormData((p) => ({ ...p, [k]: v }))} />
              </div>
            )}

            <div className="mt-2 flex justify-end gap-2">
              <button onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm">Cancel</button>
              <button onClick={submit} disabled={!ready || mutation.isPending} className="rounded-md bg-gray-900 px-4 py-2 text-sm text-white disabled:opacity-50">
                {mutation.isPending ? 'Creating…' : 'Create order'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
